import { get, create, updatePatch } from "./apiService";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface SubAccount {
  _id: string;
  accountId?: string;
  accountName?: string;
  name?: string;
  isMainAccount?: boolean;
  status?: "open" | "closed";
  accountCategory?: "personal" | "business";
  [key: string]: unknown;
}

export interface CreateSubAccountPayload {
  name: string;
}

export interface TransferPayload {
  fromAccountId: string;
  toAccountId: string;
  asset: string;
  amount: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function getStringField(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function trimName(value: unknown): string | undefined {
  const rawValue = getStringField(value)?.trim();
  return rawValue ? rawValue : undefined;
}

export function getAccountId(
  account: Pick<SubAccount, "_id" | "accountId"> | null | undefined
): string {
  const accountId = trimName(account?.accountId);
  if (accountId) {
    return accountId;
  }

  return trimName(account?._id) || "";
}

export function getAccountDisplayName(
  account:
    | Pick<SubAccount, "accountName" | "name" | "isMainAccount">
    | null
    | undefined,
  fallback = "Unnamed account"
): string {
  const normalizedName =
    trimName(account?.accountName) || trimName(account?.name);
  if (normalizedName) {
    return normalizedName;
  }

  if (account?.isMainAccount) {
    return "Main Account";
  }

  return fallback;
}

function normalizeSubAccount(account: unknown): SubAccount {
  const record = isRecord(account) ? account : {};
  const normalizedId = trimName(record.accountId) || trimName(record._id) || "";

  return {
    ...(record as SubAccount),
    _id: normalizedId,
    accountId: normalizedId,
    accountName: getAccountDisplayName(record as SubAccount),
    name: getAccountDisplayName(record as SubAccount),
  };
}

// ─── API Calls ──────────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/sub-accounts/:id
export const getSubAccount = async (id: string): Promise<SubAccount> => {
  const response = await get<SubAccount>(`/api/v2/sub-accounts/${id}`);
  return normalizeSubAccount(response);
};

// V2 ENDPOINT: POST /api/v2/sub-accounts
export const createSubAccount = async (
  data: CreateSubAccountPayload
): Promise<SubAccount> => {
  const response = await create<SubAccount>("/api/v2/sub-accounts", data);
  return normalizeSubAccount(response);
};

// V2 ENDPOINT: POST /api/v2/sub-accounts/:id/set-active
export const setActiveSubAccount = async (
  accountId: string
): Promise<unknown> => {
  return create(`/api/v2/sub-accounts/${accountId}/set-active`);
};

// V2 ENDPOINT: PATCH /api/v2/sub-accounts/:id/name
export const renameSubAccount = async (
  accountId: string,
  name: string
): Promise<unknown> => {
  return updatePatch(`/api/v2/sub-accounts/${accountId}/name`, {}, { name });
};

// V2 ENDPOINT: PATCH /api/v2/sub-accounts/:id/close
export const closeSubAccount = async (accountId: string): Promise<unknown> => {
  return updatePatch(`/api/v2/sub-accounts/${accountId}/close`);
};

// V2 ENDPOINT: POST /api/v2/sub-accounts/transfer
export const transferFunds = async (
  data: TransferPayload
): Promise<unknown> => {
  return create("/api/v2/sub-accounts/transfer", data);
};

// ─── Legacy aliases ─────────────────────────────────────────────────────────

/** @deprecated Use setActiveSubAccount */
export const setAccount = async (
  _userId: string,
  accountId: string
): Promise<unknown> => {
  return setActiveSubAccount(accountId);
};

/** @deprecated Use renameSubAccount */
export const updateAccount = async (
  _userId: string,
  accountId: string,
  name: string
): Promise<unknown> => {
  return renameSubAccount(accountId, name);
};
